import React from 'react';
import { Alert, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getActaByTemporadaAndCodActa } from '../../services/actasService';
import { reportMatchDismissals, reportPreviousMatch } from '../../utils/rivalReport';
import { matchPlayerName } from '../../utils/matchRecord';
import './reportPreviousDismissals.css';

export default function ReportPreviousDismissals({ calendar, teamId, teamName, match, example }) {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();
  const previous = reportPreviousMatch(calendar, teamId, teamName, match);
  const code = previous?.codacta ?? previous?.codActa ?? previous?.idActa;
  const season = previous?.idTemporada ?? match?.idTemporada;
  const query = useQuery({
    queryKey: ['report-previous-acta', season, code, example?.slug ?? 'private'],
    enabled: code != null && season != null,
    retry: false,
    queryFn: async () => {
      const rows = example ? example.actas : await getActaByTemporadaAndCodActa(season, code, navigate);
      if (!Array.isArray(rows)) throw new Error('Invalid previous-match acta response');
      const acta = rows.find(row => String(row.codActa ?? row.codacta) === String(code))
        ?? (!example && rows.length === 1 ? rows[0] : null);
      if (!acta) throw new Error('Previous-match acta unavailable');
      return acta;
    },
  });
  const dismissals = query.data && previous ? reportMatchDismissals(query.data, previous.home) : null;
  return <section className="rr-panel rr-previous-dismissals" aria-label={t('previousDismissals.title')}>
    <header><h2>{t('previousDismissals.title')}</h2><p>{t('previousDismissals.note')}</p></header>
    {!previous ? <p className="rr-empty">{t('previousDismissals.noPrevious')}</p> : <>
      <p>{t('previousDismissals.fixture', { opponent: previous.opponent, date: previous.fecha || '—' })}</p>
      {code == null || season == null ? <p className="rr-empty">{t('previousDismissals.unavailable')}</p>
        : query.isError ? <Alert severity="error" action={<Button onClick={() => query.refetch()}>
          {t('previousDismissals.retry')}</Button>}>{t('previousDismissals.error')}</Alert>
          : query.isPending ? <p role="status">{t('previousDismissals.loading')}</p>
            : dismissals === null ? <p className="rr-empty">{t('previousDismissals.unavailable')}</p>
              : !dismissals.length ? <p>{t('previousDismissals.none')}</p>
                : <ul>{dismissals.map((player, index) => <li key={player.id ?? index}>
                  <span className="rr-dismissal-symbol" aria-hidden="true" />
                  <strong>{matchPlayerName(player.name) || t('previousDismissals.unknownPlayer')}</strong>
                  <span>{t(`previousDismissals.${player.reason}`)}
                    {player.minute != null && player.minute !== '' ? ` · ${player.minute}′` : ''}</span>
                </li>)}</ul>}
      {!example && code != null && season != null && <Link to={`/actaPartido?${new URLSearchParams({
        idTemporada: String(season), codActa: String(code), section: 'events',
      })}`} state={{ partido: previous }}>{t('previousDismissals.openActa')}</Link>}
    </>}
  </section>;
}
